<template>
  <div
    data-component-group="threads-form"
    data-component="deal-hero"
    class="elevation-1"
  >
    <div class="panel-container">
      <div class="thread-details">
        <div class="primary-details">
          <div class="panel-box">
            <div class="panel-content">
              <div class="thread-title">
                <span>
                  <v-icon size="x-small">local_offer</v-icon>
                  {{ thread.title }}
                </span>

                <Can
                  action="see-mass-actions"
                  subject="thread"
                >
                  <dropdowns-threads-actions
                    v-if="!isLive && !isPublished"
                    :actions="threadActions"
                    :can-search="canSearch"
                    :on-change-item="onChangeAction"
                    density="compact"
                  />
                </Can>
              </div>

              <div class="item-url">
                <copy-to-clipboard
                  :text="itemUrl"
                  ripple
                  :depressed="false"
                />

                <panels-threads-primary-actions
                  v-if="!isLive"
                  :thread="thread"
                  :actions="threadActions"
                  :on-change-item="onChangeAction"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="secondary-details">
          <table class="thread-details-table">
            <thead>
              <tr>
                <td>{{ 'Created' }}</td>

                <td>{{ 'Business' }}</td>

                <td>{{ 'Status' }}</td>

                <td v-if="hasOwner">
                  {{ 'Owner' }}
                </td>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <panels-threads-submitted-date :thread="thread" />
                </td>

                <td>
                  <panels-threads-business
                    v-if="hasThread"
                    :thread="thread"
                    :show-links="!isBusinessContact"
                  />
                </td>

                <td>
                  <panels-threads-status
                    :thread="thread"
                    is-assignee-visible
                    :is-assign-to-me-visible="!isDenied"
                    :on-update-thread="onUpdateThread"
                  />
                </td>

                <td v-if="hasOwner">
                  <panels-threads-owner :owner="thread.manager" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <Can
          v-if="duplicates.length > 0"
          action="see-duplicate-deals"
          subject="thread"
        >
          <panels-threads-duplicates :duplicates="duplicates" />
        </Can>
      </div>
    </div>
  </div>
</template>

<script>
  import { isNum, isObj } from '../../../utils';
  import unacceptedActions from '../../../config/threads/actions/unaccepted';
  import acceptedActions from '../../../config/threads/actions/accepted';
  import deniedActions from '../../../config/threads/actions/denied';

  const { location } = window;

  export default {
    props: {
      thread: {
        type: Object,
        required: true,
      },
      duplicates: {
        type: Array,
        required: true,
      },
      onChangeAction: {
        type: Function,
        default: () => {},
      },
      onUpdateThread: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        unacceptedActions,
        acceptedActions,
        deniedActions,
        canSearch: true,
        isHighlighted: false,
      };
    },
    computed: {
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      isBusinessContact() {
        const role = this.$store.getters['auth/role'];
        return (
          role.slug === 'marketing_agent' ||
          role.slug === 'business_contact' ||
          role.slug === 'business_admin'
        );
      },
      hasThread() {
        return isNum(this.thread.id);
      },
      hasOwner() {
        return this.hasThread && isObj(this.thread.manager);
      },
      threadStatus() {
        return this.hasThread ? this.thread.status?.name?.toLowerCase() ?? '' : '';
      },
      isLive() {
        return this.threadStatus === 'live';
      },
      isPublished() {
        return this.threadStatus === 'published';
      },
      isAccepted() {
        return this.threadStatus === 'accepted';
      },
      isDenied() {
        return this.threadStatus === 'declined';
      },
      canModifyThread() {
        const { thread, activeUser, hasThread } = this;

        return (
          hasThread &&
          isObj(thread.assignee) &&
          thread.assignee.id === activeUser.id
        );
      },
      threadActions() {
        const {
          isAccepted,
          isDenied,
          acceptedActions,
          unacceptedActions,
          deniedActions,
          canModifyThread,
        } = this;
        let nextActions = isAccepted
          ? acceptedActions
          : isDenied
            ? deniedActions
            : unacceptedActions;

        if (!canModifyThread) {
          nextActions = nextActions.filter((action) => !action.isManage);
        }

        return nextActions;
      },
      businessName() {
        return this.hasThread ? this.thread.business.name : '';
      },
      itemUrl() {
        return `${location.origin}/threads/${this.thread.id}`;
      },
    },
    watch: {
      isDenied(nextVal) {
        this.canSearch = !nextVal;
      },
    },
    created() {
      this.canSearch = !this.isDenied;
    },
    methods: {
      getTriggerClass() {
        return {
          trigger: true,
          'view-item': true,
          'is-blocked': this.isError,
        };
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='threads-form'][data-component='deal-hero'] {
  padding: $spacing-xl;
  background-color: map.get($colors, grey, darken-4);

  .panel-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: map.get($breakpoints, sm)) {
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
    }

    .thread-details {
      width: 100%;
      margin: ($spacing-xs * 5) 0 0 0;
    }

    .primary-details {
      padding: 0 0 $spacing-sm;

      @media (max-width: map.get($breakpoints, sm)) {
        width: 100%;
      }

      [data-component='business'] {
        @media (max-width: map.get($breakpoints, sm)) {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
      }
    }

    .secondary-details {
      @media (max-width: map.get($breakpoints, sm)) {
        width: 100%;
      }
    }
  }

  .panel-box {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .panel-label {
      color: map.get($colors, grey, base);
      margin: 0 10px 0 0;
    }

    .panel-content {
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 16px;
      width: 100%;
      font-weight: bold;
    }
  }

  .secondary-details {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }


  [data-component='business'] {
    .business-name {
      font-size: $text-size-xl;
      margin: 0 0 0 -3px;

      .v-icon {
        font-size: $text-size-xl + 0.25rem;
      }
    }
  }

  [data-component='status'] {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-weight: bold;

    .assignee-box {
      margin: 0 0 0 10px;
    }
  }

  [data-component='actions'] {
    margin-bottom: 0;
    text-align: right;

    .v-menu {
      width: auto;

      .v-btn {
        .v-icon {
          margin: 0 0 0 10px;
        }
      }
    }
  }

  [data-component='business'] {
    .user-name,
    .business-name,
    .thread-panel__platform {
      color: map.get($colors, shades, 'white');

      .v-icon {
        color: map.get($colors, shades, 'white');
      }
    }
  }

  .thread-title {
    color: map.get($colors, shades, 'white');
    display: flex;
    justify-content: space-between;
    margin-right: $spacing-sm * 4;
    font-weight: 500;
    font-size: $text-size-xl;
    line-height: 32px;
    letter-spacing: 0.4px;
    width: calc(100% - #{$spacing-sm * 4});

    .v-icon {
      color: map.get($colors, shades, 'white');
    }
  }

  .item-url {
    @include panel-item-url;
  }

  .thread-details-table {
    width: 100%;

    thead {
      td {
        color: map.get($colors, grey, base);
        width: 25%;

        &:first-child {
          width: 20%;
        }

        &:nth-child(2) {
          width: 30%;
        }
      }
    }

    tbody {
      td {
        padding: 10px 0 0 0 !important;
        width: 25%;

        &:first-child {
          padding: 6px 0 0 0;
          width: 20%;
        }

        &:nth-child(2) {
          width: 30%;
        }

        &:nth-child(3) {
          padding: 6px 0 0 0;
        }

        &:last-child {
          padding: 6px 0 0 0;
        }
      }

      [data-component='submitted-date'] {
        .date {
          color: map.get($colors, shades, 'white');
        }
      }

      [data-component='business'] {
        .business-name {
          font-size: $text-size-sm;

          .v-icon {
            font-size: $text-size-xl;
          }
        }

        .user-name {
          font-size: $text-size-sm;
          margin: 0 0 0 -2px;

          .v-icon {
            font-size: $text-size-xl;
          }
        }
      }

      [data-component='status'] {
        position: relative;
        top: 3px;
        display: block;

        .assignee-box {
          margin: 0;
        }

        .status-name {
          padding: 0 6px 0 0;
        }

        .trigger.assign-user {
          color: map.get($colors, shades, 'white');
          font-size: $text-size-sm;
          font-weight: 400;
        }

        .trigger.view-url {
          height: 30px;
          min-height: 30px;
          color: map.get($colors, shades, 'white');
          margin: 0 0 0 -8px;
        }
      }

      [data-component='account-owner'] {
        margin: -24px 0 0 0;

        .contact-box {
          color: map.get($colors, shades, 'white');

          .owner-name {
            font-weight: 400;
          }

          .v-icon {
            font-size: $text-size-xl;
            color: map.get($colors, shades, 'white');
          }
        }
      }
    }
  }

  [data-component='duplicates'] {
    margin: 24px 0 0 0;
  }

  [data-component-group='thread-panels'][data-component='status'] {
    .trigger.view-url {
      margin-left: -16px !important;
    }

    .trigger.assign-user {
      margin-left: -$spacing-sm;
    }
  }
}
</style>
